import type { OpenAI } from "openai";
import { config } from "@lisapet/config";
import { io, type Socket } from "socket.io-client";
import { type ErrorObject } from "serialize-error";
import type { UIMessageChunk } from "ai";
import { supabase } from "../../../supabase";
import type { ExecutionResultMessage } from "../types/executionJobTypes";

export type PromptRunStatus = "pending" | "in_progress" | "completed" | "failed" | "canceled";

export interface PromptRunChunkMessage {
  executionId: string;
  status: PromptRunStatus;
  finish_reason: string | null;
  /** Transient live text during streaming only. */
  content?: string;
  reasoning?: string | null;
  messages?: ExecutionResultMessage[];
  usage: OpenAI.Completions.CompletionUsage | null;
  cost: { usd: number } | null;
  execution_time: { seconds: number };
}

export interface PromptRunCompletionMessage {
  executionId: string;
  status: PromptRunStatus;
  finish_reason: string | null;
  messages?: ExecutionResultMessage[];
  usage: OpenAI.Completions.CompletionUsage | null;
  cost: { usd: number } | null;
  execution_time: { seconds: number };
}

export interface PromptRunErrorMessage {
  executionId: string;
  status: PromptRunStatus;
  error: ErrorObject;
}

export interface PromptRunCancelMessage {
  executionId: string;
  status: PromptRunStatus;
}

/** Socket envelope for AI SDK UI message stream chunks. */
export interface ExecutionUIChunkEnvelope {
  executionId: string;
  runId: string | null;
  chunk: UIMessageChunk;
}

export interface UIMessageStreamSubscription {
  stream: ReadableStream<UIMessageChunk>;
  /** Cancel the underlying execution(s) and close the stream. */
  cancel: () => void;
}

type StreamFilter = { kind: "run"; runId: string } | { kind: "execution"; executionId: string };

function isTerminalChunk(chunk: UIMessageChunk): boolean {
  return chunk.type === "finish" || chunk.type === "error" || chunk.type === "abort";
}

export class PromptRunner {
  public socket: Socket | null = null;
  private lastToken: string | null = null;
  private connectionWaiters = new Set<() => void>();

  constructor() {
    this.initializeAuthListener();
  }

  private initializeAuthListener() {
    supabase.auth.onAuthStateChange((_, session) => {
      const token = session?.access_token;

      // user is signed out or no session
      if (!token) {
        this.socket?.disconnect();
        this.socket = null;
        this.lastToken = null;
        return;
      }

      // only act if token changed
      if (token !== this.lastToken) {
        this.lastToken = token;
        if (!this.socket) {
          this.socket = io(config.workers.prompt_runner.url, {
            transports: ["websocket"],
            auth: { token },
          });
          // Registered once per socket instance: reconnects (token refresh)
          // reuse it, and a sign-out/sign-in creates a new one through here.
          this.socket.on("connect", () => {
            this.connectionWaiters.forEach((resolve) => resolve());
            this.connectionWaiters.clear();
          });
        } else {
          this.socket.auth = { token };
          if (this.socket.connected) {
            this.socket.disconnect().connect();
          } else {
            this.socket.connect();
          }
        }
      }
    });
  }

  /**
   * Resolves once the socket is connected. The worker only delivers chunks to
   * a user with a live socket, so a job scheduled before the (re)connect
   * completes — right after page load or a token refresh — would stream
   * nowhere.
   */
  public waitForConnection(timeoutMs = 10_000): Promise<void> {
    if (this.socket?.connected) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const onConnect = () => {
        clearTimeout(timer);
        resolve();
      };

      const timer = setTimeout(() => {
        this.connectionWaiters.delete(onConnect);
        reject(new Error("Not connected to the runner"));
      }, timeoutMs);

      this.connectionWaiters.add(onConnect);
    });
  }

  public onChunk(callback: (message: PromptRunChunkMessage) => void) {
    if (!this.socket) throw new Error("Socket not connected");
    this.socket.on("execution:chunk", callback);
  }

  public onCompletion(callback: (message: PromptRunCompletionMessage) => void) {
    if (!this.socket) throw new Error("Socket not connected");
    this.socket.on("execution:completion", callback);
  }

  public onError(callback: (message: PromptRunErrorMessage) => void) {
    if (!this.socket) throw new Error("Socket not connected");
    this.socket.on("execution:error", callback);
  }

  public onCancel(callback: (message: PromptRunCancelMessage) => void) {
    if (!this.socket) throw new Error("Socket not connected");
    this.socket.on("execution:canceled", callback);
  }

  public onUIChunk(callback: (envelope: ExecutionUIChunkEnvelope) => void) {
    if (!this.socket) throw new Error("Socket not connected");
    this.socket.on("execution:ui-chunk", callback);
  }

  public offUIChunk(callback: (envelope: ExecutionUIChunkEnvelope) => void) {
    this.socket?.off("execution:ui-chunk", callback);
  }

  public cancelExecutionJob(job_id: string) {
    if (!this.socket) throw new Error("Socket not connected");
    this.socket.emit("execution:cancel", job_id);
  }

  /**
   * Subscribe to UI message chunks for a prompt/eval run (all jobs under that runId).
   * Playground conversation typically has one job per run.
   */
  public subscribeToRun(runId: string): UIMessageStreamSubscription {
    return this.subscribeUIMessageStream({ kind: "run", runId });
  }

  /**
   * Subscribe to UI message chunks for a single execution job.
   */
  public subscribeToExecution(executionId: string): UIMessageStreamSubscription {
    return this.subscribeUIMessageStream({ kind: "execution", executionId });
  }

  private subscribeUIMessageStream(filter: StreamFilter): UIMessageStreamSubscription {
    if (!this.socket) throw new Error("Socket not connected");

    const seenExecutionIds = new Set<string>();
    let controller: ReadableStreamDefaultController<UIMessageChunk> | null = null;
    let closed = false;

    const matches = (envelope: ExecutionUIChunkEnvelope) => {
      if (filter.kind === "run") {
        return envelope.runId === filter.runId;
      }
      return envelope.executionId === filter.executionId;
    };

    const closeStream = () => {
      if (closed) return;
      closed = true;
      this.socket?.off("execution:ui-chunk", onEnvelope);
      this.socket?.off("execution:canceled", onCanceled);
      this.socket?.off("execution:error", onError);
      try {
        controller?.close();
      } catch {
        // already closed
      }
    };

    const onEnvelope = (envelope: ExecutionUIChunkEnvelope) => {
      if (closed || !matches(envelope)) return;
      seenExecutionIds.add(envelope.executionId);
      try {
        controller?.enqueue(envelope.chunk);
      } catch {
        // stream already closed
        return;
      }
      if (isTerminalChunk(envelope.chunk)) {
        closeStream();
      }
    };

    const onCanceled = (message: PromptRunCancelMessage) => {
      if (closed) return;
      if (filter.kind === "execution" && message.executionId !== filter.executionId) return;
      if (filter.kind === "run" && !seenExecutionIds.has(message.executionId)) return;
      try {
        controller?.enqueue({ type: "abort" });
      } catch {
        // ignore
      }
      closeStream();
    };

    const onError = (message: PromptRunErrorMessage) => {
      if (closed) return;
      if (filter.kind === "execution" && message.executionId !== filter.executionId) return;
      if (
        filter.kind === "run" &&
        !seenExecutionIds.has(message.executionId) &&
        seenExecutionIds.size > 0
      ) {
        return;
      }
      // For run filter before first chunk: accept errors for any job once we know
      // nothing yet — only if executionId is unknown we still forward (initiate
      // may fail before chunks). Prefer matching when we have seen ids.
      if (
        filter.kind === "run" &&
        seenExecutionIds.size > 0 &&
        !seenExecutionIds.has(message.executionId)
      ) {
        return;
      }
      try {
        controller?.enqueue({
          type: "error",
          errorText: message.error.message ?? "Execution failed",
        });
      } catch {
        // ignore
      }
      closeStream();
    };

    const stream = new ReadableStream<UIMessageChunk>({
      start: (ctrl) => {
        controller = ctrl;
      },
      cancel: () => {
        closeStream();
      },
    });

    this.socket.on("execution:ui-chunk", onEnvelope);
    this.socket.on("execution:canceled", onCanceled);
    this.socket.on("execution:error", onError);

    return {
      stream,
      cancel: () => {
        for (const executionId of seenExecutionIds) {
          try {
            this.cancelExecutionJob(executionId);
          } catch {
            // socket may already be down
          }
        }
        closeStream();
      },
    };
  }
}
